/**
 * Renders a translation feed item — a translation is its own post (see
 * Post.translationOfPostId), paired with the canonical post it translates.
 *
 * `hideOriginal` — omits the "See original" toggle; set when the card is already rendered
 * inside PostCard's translation list so the original post isn't shown twice.
 *
 * Engagement on a translation: boost is independent (see BoostButton below), clap is shown
 * but always credits the canonical post server-side, and replies are disabled entirely —
 * discussion happens on the canonical post, reached via "view original".
 */
import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Globe, ChevronDown, ChevronUp, Flag, Trash2, Loader2, ExternalLink } from 'lucide-react'
import { ReportModal } from '@/components/common/ReportModal'
import { ShareButton } from './ShareButton'
import type { Translation, Post, EventPost, OfferPost, FeedItem } from '@olimhub/shared'
import { PostCard } from './PostCard'
import { EventMeta } from './EventMeta'
import { PostBadge } from './PostBadge'
import { LanguageBadge } from '@/components/common/LanguageBadge'
import { renderContent } from '@/lib/content-renderer'
import { formatPostTime } from '@/lib/dates'
import { accountPath } from '@/lib/accountPath'
import { Avatar } from '@/components/profile/Avatar'
import { BoostButton } from './BoostButton'
import { ClapButton } from './ClapButton'
import { useAuthStore } from '@/store/auth.store'
import { postsApi } from '@/api'
import { useToast } from '@/components/common/Toast'

interface InfiniteData {
  pages: Array<{ items: FeedItem[]; nextCursor: string | null }>
  pageParams: unknown[]
}

interface TranslationCardProps {
  translation: Translation
  readOnly?: boolean
  hideOriginal?: boolean
  isDetailPage?: boolean
  onBoost?: (id: string) => void
  onReport?: (id: string) => void
}

export function TranslationCard({ translation, readOnly, hideOriginal, isDetailPage = false, onBoost, onReport }: TranslationCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { account: authAccount } = useAuthStore()
  const [showOriginal, setShowOriginal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)

  const { post, originalPost } = translation
  const isOwn = !!authAccount && authAccount.id === post.account.id
  const translationHtml = renderContent(post.content)

  const isEvent = post.type === 'event'
  const isOffer = post.type === 'offer'
  const accentColor = isEvent ? 'var(--accent)' : isOffer ? 'var(--bazaar)' : 'var(--brand)'
  const borderStartColor = isEvent || isOffer ? accentColor : undefined

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await postsApi.delete(post.id)
      toast.success('Translation removed')
      const removeFromPages = (old: InfiniteData | undefined) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map(page => ({
            ...page,
            items: page.items.filter(
              item => !(item.kind === 'translation' && (item.data as Translation).post.id === post.id),
            ),
          })),
        }
      }
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['feed'] }, removeFromPages)
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['profile-feed'] }, removeFromPages)
      queryClient.invalidateQueries({ queryKey: ['post-translations', originalPost.id] })
      queryClient.invalidateQueries({ queryKey: ['search', 'posts'] })
    } catch {
      toast.error('Could not remove translation.')
    } finally {
      setIsDeleting(false)
      setConfirmDelete(false)
    }
  }

  return (
    <div
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        border: '1px dashed var(--border)',
        overflow: 'hidden',
        ...(borderStartColor
          ? {
              borderInlineStartWidth: 4,
              borderInlineStartStyle: 'solid',
              borderInlineStartColor: borderStartColor,
            }
          : {}),
      }}
    >
      {/* Translation header band */}
      <div
        style={{
          background: `linear-gradient(to right, transparent, ${accentColor}, transparent) bottom / 100% 2px no-repeat`,
          border: 'none',
          borderRadius: 'calc(var(--radius-md) - 1px) calc(var(--radius-md) - 1px) 0 0',
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        <Globe size={13} color={accentColor} style={{ flexShrink: 0 }} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 4,
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 600,
            color: 'var(--text-muted)',
          }}
        >
          <button
            type="button"
            onClick={() => navigate(accountPath(post.account))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-body)' }}
          >
            @{post.account.username}
          </button>
          <span>{t('post.translated')}</span>
          <button
            type="button"
            onClick={() => navigate(accountPath(originalPost.account))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-body)' }}
          >
            @{originalPost.account.username}
          </button>
        </div>
      </div>

      {/* Header */}
      <div style={{ padding: '14px 16px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <button
          type="button"
          onClick={() => navigate(accountPath(post.account))}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex' }}
        >
          <Avatar
            name={post.account.displayName || post.account.username}
            avatarUrl={post.account.avatarUrl}
            animalAvatar={post.account.animalAvatar}
            accountType={post.account.accountType}
            size="md"
          />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={() => navigate(accountPath(post.account))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)', fontWeight: 700, color: 'var(--text-strong)', fontSize: 'var(--fs-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, textAlign: 'start' }}
            >
              {post.account.displayName || post.account.username}
            </button>
            <PostBadge badges={post.account.badges} />
            <div style={{ marginInlineStart: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <LanguageBadge lang={post.language} size="sm" />
              {isOwn && !readOnly && (
                <button
                  type="button"
                  aria-label="Remove translation"
                  onClick={() => setConfirmDelete(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', border: 'none', background: 'none', cursor: 'pointer', padding: 4, borderRadius: 'var(--radius-sm)', color: confirmDelete ? 'var(--danger)' : 'var(--text-muted)' }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <button
              type="button"
              onClick={() => navigate(accountPath(post.account))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
            >
              @{post.account.username}
            </button>
            <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}>·</span>
            <Link
              to={`/posts/${originalPost.id}`}
              style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-faint)', flexShrink: 0, textDecoration: 'none' }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
            >
              {formatPostTime(post.createdAt)}
            </Link>
          </div>
        </div>
      </div>

      {/* Translated content */}
      <div style={{ padding: '10px 16px 0' }}>
        <div
          dangerouslySetInnerHTML={{ __html: translationHtml }}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            color: 'var(--text-body)',
            lineHeight: 'var(--lh-normal)',
            wordBreak: 'break-word',
          }}
        />
      </div>

      {/* Event meta — the translation's own event row (own placeText, same when/category) */}
      {isEvent && (post as EventPost).event && <EventMeta event={(post as EventPost).event} category={post.category} />}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div
          style={{
            margin: '10px 16px 0',
            padding: '12px 14px',
            background: 'color-mix(in srgb, var(--danger) 8%, var(--surface))',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, var(--border))',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
            {t('post.remove_translation_confirm')}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              style={{ padding: '7px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', cursor: 'pointer' }}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              disabled={isDeleting}
              onClick={handleDelete}
              style={{ padding: '7px 14px', border: 'none', borderRadius: 'var(--radius-sm)', background: 'var(--danger)', color: '#fff', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: isDeleting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: isDeleting ? 0.7 : 1 }}
            >
              {isDeleting && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
              {t('post.remove_translation')}
            </button>
          </div>
        </div>
      )}

      {/* See original toggle + expanded — hidden when already rendered inside the original post */}
      {!hideOriginal && (
        <div style={{ padding: '8px 16px 0' }}>
          <button
            type="button"
            onClick={() => setShowOriginal((v) => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', cursor: 'pointer', padding: '2px 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}
          >
            {showOriginal ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {showOriginal ? t('post.hide_original') : t('post.see_original')}
          </button>
          {isDetailPage && (
            <Link
              to={`/posts/${originalPost.id}`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                marginInlineStart: 12,
                fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)',
                color: 'var(--text-muted)', textDecoration: 'none',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--brand)' }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)' }}
            >
              <ExternalLink size={13} />
              {t('post.view_original_post')}
            </Link>
          )}
          {showOriginal && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
              <PostCard
                post={originalPost as Post | EventPost | OfferPost}
                readOnly={readOnly}
                isNested
                onBoost={onBoost}
                onReport={onReport}
              />
            </div>
          )}
        </div>
      )}

      {/* Action row — boost is independent per translation; clap credits the canonical
          post server-side; replies are disabled (discussion stays on the canonical post) */}
      <div style={{ padding: '6px 10px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
        <BoostButton
          postId={post.id}
          boostId={null}
          count={post.boostCount}
          isBoosted={post.isBoosted ?? false}
        />
        {!isOwn && (
          <ClapButton postId={post.id} clapped={post.clapped ?? false} />
        )}
        <ShareButton
          postId={post.id}
          authorUsername={post.account.username}
          contentPreview={post.content}
        />
        {authAccount && !isOwn && (
          <button
            type="button"
            aria-label="Report"
            onClick={() => setShowReportModal(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 9px', border: 'none', background: 'none', cursor: 'pointer', borderRadius: 'var(--radius-pill)', color: 'var(--text-muted)', marginInlineStart: 'auto' }}
          >
            <Flag size={16} />
          </button>
        )}
      </div>

      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        postId={post.id}
      />
    </div>
  )
}
